import { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { persistenceService } from '../services/persistenceService';

interface UseAutoSaveOptions {
  delay?: number; // Delay in milliseconds (default: 2000ms = 2 seconds)
  enabled?: boolean; // Whether auto-save is enabled (default: true)
  showToasts?: boolean; // Whether to show toast notifications (default: false for auto-save)
}

export const useAutoSave = (options: UseAutoSaveOptions = {}) => {
  const { delay = 2000, enabled = true, showToasts = false } = options;
  const { state } = useAppContext();
  const [isSaving, setIsSaving] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const lastSavedContentRef = useRef<string>('');
  const lastSaveTimeRef = useRef<Date>(new Date());

  useEffect(() => {
    // Skip if auto-save is disabled or if preferences indicate it's disabled
    if (!enabled || !state.preferences.autoSave) {
      return;
    }

    // Skip if content hasn't changed since last save
    if (lastSavedContentRef.current === state.document.content) {
      return;
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set up new timeout for auto-save
    timeoutRef.current = window.setTimeout(async () => {
      setIsSaving(true);
      
      try {
        // Save document using enhanced persistence service
        const documentToSave = {
          ...state.document,
          lastModified: new Date(),
        };

        persistenceService.saveDocument(documentToSave);
        
        // Update references
        lastSavedContentRef.current = state.document.content;
        lastSaveTimeRef.current = new Date();

        // Add to document history for version tracking
        persistenceService.addToHistory(documentToSave);

        console.log('Document auto-saved successfully');
        
        // Optional: Show success toast (usually disabled for auto-save to avoid spam)
        if (showToasts && typeof window !== 'undefined') {
          // Only show toasts if explicitly enabled
          const event = new CustomEvent('show-toast', {
            detail: {
              type: 'success',
              message: 'Document saved automatically',
              duration: 2000
            }
          });
          window.dispatchEvent(event);
        }
      } catch (error) {
        console.error('Auto-save failed:', error);
        
        // Always show error toasts
        if (typeof window !== 'undefined') {
          const event = new CustomEvent('show-toast', {
            detail: {
              type: 'error',
              title: 'Auto-save Failed',
              message: 'Unable to save your changes. Please save manually.',
              duration: 0, // Persist error until dismissed
              action: {
                label: 'Retry',
                onClick: () => {
                  // Trigger another save attempt
                  const retryEvent = new CustomEvent('manual-save-request');
                  window.dispatchEvent(retryEvent);
                }
              }
            }
          });
          window.dispatchEvent(event);
        }
      } finally {
        setIsSaving(false);
      }
    }, delay);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [state.document.content, state.preferences.autoSave, enabled, delay, showToasts]);

  // Manual save function
  const manualSave = async () => {
    if (isSaving) return false;
    
    setIsSaving(true);
    
    try {
      const documentToSave = {
        ...state.document,
        lastModified: new Date(),
      };

      persistenceService.saveDocument(documentToSave);
      lastSavedContentRef.current = state.document.content;
      lastSaveTimeRef.current = new Date();
      persistenceService.addToHistory(documentToSave);

      if (typeof window !== 'undefined') {
        const event = new CustomEvent('show-toast', {
          detail: {
            type: 'success',
            message: 'Document saved successfully',
            duration: 3000
          }
        });
        window.dispatchEvent(event);
      }

      return true;
    } catch (error) {
      console.error('Manual save failed:', error);
      
      if (typeof window !== 'undefined') {
        const event = new CustomEvent('show-toast', {
          detail: {
            type: 'error',
            title: 'Save Failed', 
            message: error instanceof Error ? error.message : 'Unable to save document',
            duration: 0
          }
        });
        window.dispatchEvent(event);
      }
      
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Return utility functions and state
  return {
    lastSaveTime: lastSaveTimeRef.current,
    isAutoSaveEnabled: enabled && state.preferences.autoSave,
    hasUnsavedChanges: lastSavedContentRef.current !== state.document.content,
    isSaving,
    manualSave,
  };
};